// Admin Spaces store — signal-based, follows departments.ts pattern.
// GET /api/admin/spaces → AdminSpace[] with embedded members.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { api } from "../lib/api";

export interface AdminSpaceMember {
  user_id: number;
  name: string;
  email: string;
  role: "viewer" | "editor";
}

export interface AdminSpaceDept {
  department_id: number;
  name: string;
  role: "viewer" | "editor";
}

export interface AdminSpace {
  id: number;
  name: string;
  slug: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  project_count: number;
  member_count: number;
  members: AdminSpaceMember[];
  departments: AdminSpaceDept[];
}

interface State {
  loading: boolean;
  items: AdminSpace[];
  error: string | null;
}

const initial: State = { loading: true, items: [], error: null };
const adminSpacesSignal: Signal<State> = signal(initial);
let inFlight: Promise<void> | null = null;

async function load(): Promise<void> {
  try {
    const items = await api<AdminSpace[]>("/api/admin/spaces");
    adminSpacesSignal.value = { loading: false, items, error: null };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Unknown error";
    adminSpacesSignal.value = { loading: false, items: [], error: msg };
  } finally {
    inFlight = null;
  }
}

export function useAdminSpaces(): State {
  useEffect(() => {
    if (!inFlight && adminSpacesSignal.value.loading) {
      inFlight = load();
    }
  }, []);
  return adminSpacesSignal.value;
}

export async function refreshAdminSpaces(): Promise<void> {
  adminSpacesSignal.value = { ...adminSpacesSignal.value, loading: true };
  inFlight = load();
  return inFlight;
}

export async function createAdminSpace(body: {
  name: string;
  icon?: string | null;
  color?: string | null;
}): Promise<AdminSpace> {
  const space = await api<AdminSpace>("/api/admin/spaces", {
    method: "POST",
    body,
  });
  await refreshAdminSpaces();
  return space;
}

export async function assignAdminSpaceMember(
  spaceId: number,
  user_id: number,
  role: "viewer" | "editor"
): Promise<void> {
  await api(`/api/admin/spaces/${spaceId}/members`, {
    method: "POST",
    body: { user_id, role },
  });
  await refreshAdminSpaces();
}

export async function removeAdminSpaceMember(
  spaceId: number,
  userId: number
): Promise<void> {
  await api(`/api/admin/spaces/${spaceId}/members/${userId}`, {
    method: "DELETE",
  });
  await refreshAdminSpaces();
}

export async function renameAdminSpace(
  id: number,
  patch: { name?: string; icon?: string | null; color?: string | null }
): Promise<void> {
  await api(`/api/admin/spaces/${id}`, { method: "PATCH", body: patch });
  await refreshAdminSpaces();
}

export async function grantAdminSpaceDepartment(
  spaceId: number,
  department_id: number,
  role: "viewer" | "editor"
): Promise<void> {
  await api(`/api/admin/spaces/${spaceId}/departments`, {
    method: "POST",
    body: { department_id, role },
  });
  await refreshAdminSpaces();
}

export async function revokeAdminSpaceDepartment(
  spaceId: number,
  department_id: number
): Promise<void> {
  await api(`/api/admin/spaces/${spaceId}/departments/${department_id}`, {
    method: "DELETE",
  });
  await refreshAdminSpaces();
}
